import { PackageSearch } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { WorkspaceSidebarFooter } from '../../../forge/components/layout/WorkspaceSidebarFooter';
import { ForgeTheme } from '../../../forge/hooks/useForgeTheme';
import { useLurkerAccess } from '../../hooks/useLurkerAccess';
import { lurkerNavItemFor, LURKER_NAV_SECTIONS } from './lurkerNavigation';

interface LurkerSidebarProps {
  theme: ForgeTheme;
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  /** Takes the sidebar off the page entirely; the layout's reveal handle brings it back. */
  hideSidebar: () => void;
  onDragStart: (event: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
  isMobile?: boolean;
  onMobileNavigate?: () => void;
}

export const LurkerSidebar: React.FC<LurkerSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  hideSidebar,
  onDragStart,
  toggleTheme,
  currentPath,
  isMobile = false,
  onMobileNavigate,
}) => {
  const { owner } = useLurkerAccess();
  const effectiveCollapsed = isCollapsed && !isMobile;
  const active = lurkerNavItemFor(currentPath);
  const sidebarClasses = [
    'forge-sidebar',
    'lurker-sidebar',
    effectiveCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
    isMobile ? 'is-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={sidebarClasses}
      style={{ width: !isMobile && !effectiveCollapsed ? width : undefined }}
      aria-label="Lurker navigation"
    >
      <div className="lurker-sidebar-brand">
        <PackageSearch aria-hidden="true" />
        <span>Lurker</span>
      </div>
      <nav className="sidebar-nav" aria-label="Lurker navigation">
        {LURKER_NAV_SECTIONS.map((section) => (
          <div key={section.title} className="sidebar-section">
            <div className="sidebar-section-label">{section.title}</div>
            {section.items
              .filter((item) => owner || !item.ownerOnly)
              .map((item) => {
                const Icon = item.icon;
                const isActive = item === active;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    aria-label={effectiveCollapsed ? item.label : undefined}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={onMobileNavigate}
                  >
                    <Icon aria-hidden="true" />
                    <span className="sidebar-item-label">{item.label}</span>
                  </Link>
                );
              })}
          </div>
        ))}
      </nav>

      <WorkspaceSidebarFooter
        theme={theme}
        toggleTheme={toggleTheme}
        collapsed={effectiveCollapsed}
        isMobile={isMobile}
        hideSidebar={hideSidebar}
        onMobileNavigate={onMobileNavigate}
      />
      {!isMobile ? (
        // A pointer-only resize grip, as in the other workspaces; Hide covers the keyboard.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="sidebar-drag-handle" onMouseDown={onDragStart} />
      ) : null}
    </aside>
  );
};
